import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Rocket, CheckCircle2, Loader2, UserCheck, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  { code: "EA", label: "EQUIPR Allocate" },
  { code: "EM", label: "EQUIPR Maintain" },
  { code: "EO", label: "EQUIPR Operator" },
  { code: "EC", label: "EQUIPR Core" },
  { code: "EG", label: "EQUIPR Guardian" },
  { code: "ER", label: "EQUIPR Remote" },
  { code: "EH", label: "EQUIPR Horizon" },
];

const TRACKS = [
  { key: "GUIDED", label: "Guided", hint: "Full hands-on onboarding" },
  { key: "LAUNCH_LITE", label: "Launch Lite", hint: "Lighter, self-serve path" },
];

export default function ConvertToClientDialog({ prospect, open, onOpenChange, staff = [], onDone }) {
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [track, setTrack] = useState("GUIDED");
  const [ownerId, setOwnerId] = useState("");
  const [goLive, setGoLive] = useState("");
  const [notes, setNotes] = useState("");
  const [createKickoff, setCreateKickoff] = useState(true);

  useEffect(() => {
    if (open) {
      setProducts(prospect?.signup_product ? [] : []);
      setTrack("GUIDED"); setOwnerId(""); setGoLive(""); setNotes(""); setCreateKickoff(true);
    }
  }, [open, prospect]);

  const toggleProduct = (code) =>
    setProducts((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);

  const ownerName = (staff.find((s) => s.id === ownerId) || {}).name;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/sales/prospects/${prospect.id}/convert`, {
        products, track, csm_owner_id: ownerId || null,
        target_go_live: goLive || null, handover_notes: notes || null,
        create_kickoff: createKickoff,
      });
      toast.success(`${prospect.company_name} converted → onboarding started`);
      onOpenChange(false);
      onDone?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally {
      setSaving(false);
    }
  };

  if (!prospect) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Rocket className="w-4 h-4 text-[#0E6B3F]" /> Convert to Client — {prospect.company_name}
          </DialogTitle>
          <p className="text-xs text-[#8A857A]">Sets up the onboarding journey. This is the handover from sales to Customer Success.</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Products */}
          <div>
            <label className="text-xs font-semibold text-[#59554D] flex items-center gap-1 mb-1.5">
              <Package className="w-3.5 h-3.5" /> Products being onboarded
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {PRODUCTS.map((p) => {
                const on = products.includes(p.code);
                return (
                  <button key={p.code} type="button" onClick={() => toggleProduct(p.code)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${on ? "border-[#0E6B3F] bg-[#E3F3E9] text-[#0E6B3F]" : "border-[#DAD5CA] bg-[#FBFAF7] text-[#59554D] hover:bg-[#F4F2ED]"}`}
                    data-testid={`convert-product-${p.code}`}>
                    <Checkbox checked={on} className="pointer-events-none" />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Track */}
          <div>
            <label className="text-xs font-semibold text-[#59554D] mb-1.5 block">Onboarding track</label>
            <div className="grid grid-cols-2 gap-2">
              {TRACKS.map((t) => {
                const on = track === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setTrack(t.key)}
                    className={`rounded-lg border px-3 py-2 text-left transition-colors ${on ? "border-[#0E6B3F] bg-[#E3F3E9]" : "border-[#DAD5CA] bg-[#FBFAF7] hover:bg-[#F4F2ED]"}`}
                    data-testid={`convert-track-${t.key}`}>
                    <div className={`text-sm font-medium ${on ? "text-[#0E6B3F]" : "text-[#1A1A1A]"}`}>{t.label}</div>
                    <div className="text-[11px] text-[#8A857A]">{t.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#59554D] flex items-center gap-1 mb-1.5">
                <UserCheck className="w-3.5 h-3.5" /> CSM owner
              </label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="convert-owner">
                  <SelectValue placeholder="Who's running it?" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#59554D] mb-1.5 block">Target go-live</label>
              <Input type="date" value={goLive} onChange={(e) => setGoLive(e.target.value)}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="convert-golive" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#59554D] mb-1.5 block">Handover notes (sales → CS)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Context, expectations, key contacts, anything CS should know before kickoff…"
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="convert-notes" />
          </div>

          {/* Kickoff action */}
          <div className="rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={createKickoff} onCheckedChange={(v) => setCreateKickoff(!!v)}
                className="mt-0.5" data-testid="convert-kickoff-toggle" />
              <div>
                <div className="text-sm font-medium text-[#1A1A1A]">
                  Create a Kickoff action{ownerName ? ` for ${ownerName}` : ""}
                </div>
                <div className="text-[11px] text-[#8A857A]">Due today, linked to the client — lands straight in their My Work.</div>
              </div>
            </label>
            {createKickoff && (
              <ul className="mt-2 ml-7 space-y-0.5 text-[11px] text-[#59554D] list-disc list-inside">
                <li>Schedule the kickoff call with the client</li>
                <li>Confirm scope &amp; the products selected above</li>
                <li>Walk through the onboarding plan &amp; target go-live</li>
                <li>Kick off data import / configuration checklist</li>
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
            className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]"
            data-testid="convert-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving}
            className="bg-[#0E6B3F] hover:bg-[#0b5733] text-white font-medium" data-testid="convert-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Convert &amp; start onboarding
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
